import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Profile, ReactionType, PostLike } from '../types/database';
import { supabase } from '../lib/supabase';
import ReactionPicker, { getReactionConfig, ReactionIcon } from './ReactionPicker';

interface PostInteractionsProps {
  postId: string;
  likesCount: number;
  commentsCount: number;
  userReactionType?: ReactionType | null;
  reactionCounts?: Record<ReactionType, number>;
  onReaction: (reactionType: ReactionType) => void;
  onComment: () => void;
}

interface ReactionWithProfile extends PostLike {
  profiles: Profile;
}

export default function PostInteractions({
  postId,
  likesCount,
  commentsCount,
  userReactionType,
  reactionCounts,
  onReaction,
  onComment
}: PostInteractionsProps) {
  const navigate = useNavigate();
  const [showReactionsList, setShowReactionsList] = useState(false);
  const [showCommenters, setShowCommenters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reactions, setReactions] = useState<ReactionWithProfile[]>([]);
  const [commenters, setCommenters] = useState<Profile[]>([]);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setShowReactionsList(false);
        setShowCommenters(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleReactionsClick = async () => {
    if (likesCount === 0) return;

    try {
      setLoading(true);
      setShowReactionsList(true);
      setShowCommenters(false);

      const { data, error } = await supabase
        .from('post_likes')
        .select(`
          id,
          reaction_type,
          created_at,
          profiles (
            id,
            full_name,
            avatar_url
          )
        `)
        .eq('post_id', postId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setReactions(data as ReactionWithProfile[] || []);
    } catch (err) {
      console.error('Error fetching reactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCommentsClick = async () => {
    if (commentsCount === 0) return;

    try {
      setLoading(true);
      setShowCommenters(true);
      setShowReactionsList(false);

      const { data, error } = await supabase
        .from('comments')
        .select(`
          profiles (
            id,
            full_name,
            avatar_url
          )
        `)
        .eq('post_id', postId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const uniqueCommenters = data?.reduce((acc: Profile[], comment) => {
        if (comment.profiles && !acc.some(p => p.id === comment.profiles.id)) {
          acc.push(comment.profiles);
        }
        return acc;
      }, []) || [];

      setCommenters(uniqueCommenters);
    } catch (err) {
      console.error('Error fetching commenters:', err);
    } finally {
      setLoading(false);
    }
  };

  const getAvatarUrl = (profile: Profile) => {
    if (!profile) return `https://api.dicebear.com/7.x/initials/svg?seed=anonymous`;

    if (profile.avatar_url) {
      if (profile.avatar_url.startsWith('http')) {
        return profile.avatar_url;
      }
      const { data } = supabase.storage.from('avatars').getPublicUrl(profile.avatar_url);
      return data.publicUrl;
    }

    return `https://api.dicebear.com/7.x/initials/svg?seed=${profile.full_name}`;
  };

  const groupedReactions = reactions.reduce((acc, reaction) => {
    const type = reaction.reaction_type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(reaction);
    return acc;
  }, {} as Record<ReactionType, ReactionWithProfile[]>);

  const topReactions = reactionCounts
    ? Object.entries(reactionCounts)
        .filter(([_, count]) => count > 0)
        .sort(([_, a], [__, b]) => b - a)
        .slice(0, 3)
        .map(([type]) => type as ReactionType)
    : [];

  return (
    <div className="space-y-2">
      {(likesCount > 0 || commentsCount > 0) && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          {likesCount > 0 && (
            <button
              onClick={handleReactionsClick}
              className="flex items-center space-x-1 hover:underline"
            >
              {topReactions.length > 0 && (
                <div className="flex -space-x-1 mr-1">
                  {topReactions.map((type) => (
                    <div key={type} className="bg-white rounded-full p-0.5">
                      <ReactionIcon type={type} className="h-4 w-4" />
                    </div>
                  ))}
                </div>
              )}
              <span>{likesCount} {likesCount === 1 ? 'Reaction' : 'Reactions'}</span>
            </button>
          )}
          {commentsCount > 0 && (
            <button
              onClick={handleCommentsClick}
              className="hover:underline ml-auto"
            >
              {commentsCount} {commentsCount === 1 ? 'Comment' : 'Comments'}
            </button>
          )}
        </div>
      )}

      <div className="flex items-center justify-between border-t border-gray-100 pt-2">
        <ReactionPicker
          currentReaction={userReactionType}
          onReactionSelect={onReaction}
        />

        <button
          onClick={onComment}
          className="flex items-center space-x-2 px-4 py-2 text-gray-500 hover:bg-gray-50 rounded-lg transition-colors"
        >
          <MessageSquare className="h-5 w-5" />
          <span>Comment</span>
        </button>
      </div>

      {showReactionsList && (
        <div
          ref={popupRef}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowReactionsList(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Reactions</h3>
            </div>

            <div className="overflow-y-auto flex-1 p-4">
              {loading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                </div>
              ) : reactions.length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(groupedReactions).map(([type, reactionList]) => {
                    const config = getReactionConfig(type as ReactionType);
                    return (
                      <div key={type} className="space-y-2">
                        <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                          <ReactionIcon type={type as ReactionType} className="h-5 w-5" />
                          <span>{config.label} ({reactionList.length})</span>
                        </div>
                        <div className="space-y-2 ml-7">
                          {reactionList.map((reaction) => (
                            <div
                              key={reaction.id}
                              onClick={() => {
                                navigate(`/user/${reaction.profiles.id}`);
                                setShowReactionsList(false);
                              }}
                              className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                              <img
                                src={getAvatarUrl(reaction.profiles)}
                                alt={reaction.profiles.full_name}
                                className="h-8 w-8 rounded-full object-cover bg-gray-100"
                                onError={(e) => {
                                  const img = e.target as HTMLImageElement;
                                  img.src = `https://api.dicebear.com/7.x/initials/svg?seed=${reaction.profiles.full_name}`;
                                }}
                              />
                              <span className="text-sm">{reaction.profiles.full_name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-gray-500">No reactions yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {showCommenters && (
        <div
          ref={popupRef}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCommenters(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <MessageSquare className="h-5 w-5 text-blue-500" />
                <h3 className="text-lg font-semibold">Commenters</h3>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto p-4">
              {loading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                </div>
              ) : commenters.length > 0 ? (
                <div className="space-y-4">
                  {commenters.map((profile) => (
                    <div
                      key={profile.id}
                      onClick={() => {
                        navigate(`/user/${profile.id}`);
                        setShowCommenters(false);
                      }}
                      className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <img
                        src={getAvatarUrl(profile)}
                        alt={profile.full_name}
                        className="h-10 w-10 rounded-full object-cover bg-gray-100"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          img.src = `https://api.dicebear.com/7.x/initials/svg?seed=${profile.full_name}`;
                        }}
                      />
                      <span className="font-medium">{profile.full_name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500">No comments yet</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
